import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTransition } from './TransitionContext';
import { cn } from '@/lib/utils';

interface SharedElementProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  layoutId?: string; // Optional override for layout animation ID
  data?: any; // Additional data to be stored with this element
  disabled?: boolean; // Disable transition functionality
  as?: React.ElementType; // Component type to render
}

const SharedElement: React.FC<SharedElementProps> = ({
  id,
  children,
  className,
  onClick,
  style,
  layoutId,
  data,
  disabled = false,
  as = 'div',
}) => {
  const { registerItem, isActive } = useTransition();
  const ref = useRef<HTMLDivElement>(null);
  const Component = as;
  
  // Register this element for transition measurements
  useEffect(() => {
    try {
      if (ref.current && !disabled) {
        registerItem(id, ref.current, data);
      }
    } catch (error) {
      console.error('Error registering shared element:', error);
    }
  }, [id, disabled, data]);  // Remove registerItem from deps to avoid re-render loops

  // Set up the props for the component
  const sharedProps = {
    ref,
    className: cn(className),
    style,
    onClick,
    'data-shared-id': id,
  };

  // For elements that don't need motion features
  if (disabled) {
    return <Component {...sharedProps}>{children}</Component>;
  }

  return (
    <motion.div
      {...sharedProps}
      layoutId={layoutId || id}
      layout
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }}
    >
      {children}
    </motion.div>
  );
};

export default SharedElement;
